import { useCallback, useState, useEffect, ReactNode } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { CurrentUserContext, type CurrentUserState, type UserType } from './useCurrentUser.context';

// Type-only re-exports are erased at compile time and do not break Fast
// Refresh, so the rule allows them alongside the Provider component below.
// Runtime consumers (the `useCurrentUser` hook itself) must import from
// `./useCurrentUser.context` directly.
export type { CurrentUserState, UserType } from './useCurrentUser.context';

/**
 * Provides the current authenticated user's derived attributes (display name,
 * email, user type, admin flag) from the Cognito id token.
 *
 * The attributes are read once on mount and re-read whenever Amplify emits an
 * `auth` Hub event that indicates the token changed (`tokenRefresh`) or a new
 * sign-in occurred (`signedIn`). This keeps the nav and any other consumers in
 * sync after a role change in Cognito without requiring a full page reload —
 * the next token refresh carries the updated `custom:userType` claim and the
 * provider re-derives state from it.
 */
export function CurrentUserProvider({ children }: { children: ReactNode }) {
    const [state, setState] = useState<CurrentUserState>({
        displayName: null,
        email: null,
        userType: null,
        isAdmin: false,
        loading: true,
    });

    /**
     * Reads the current auth session and derives the user state from the id
     * token payload. Stable across re-renders (empty deps) so it can be listed
     * in effect dependency arrays and invoked imperatively from the Hub
     * listener without re-registering. Only calls `setState`; it does not own
     * any in-flight cancellation guard because the provider lives for the whole
     * authenticated session.
     */
    const refetchUser = useCallback(async (): Promise<void> => {
        try {
            const session = await fetchAuthSession();
            const payload = session.tokens?.idToken?.payload;

            const givenName = payload?.given_name as string | undefined;
            const familyName = payload?.family_name as string | undefined;
            const email = payload?.email as string | undefined;
            const parts = [givenName, familyName].filter(Boolean);
            const name = parts.join(' ') || email || '';

            const rawUserType = payload?.['custom:userType'] as string | undefined;
            const userType: UserType | null = rawUserType === 'admin' || rawUserType === 'signer' ? rawUserType : null;

            setState({
                displayName: name || null,
                email: email || null,
                userType,
                isAdmin: userType === 'admin',
                loading: false,
            });
        } catch {
            setState({
                displayName: null,
                email: null,
                userType: null,
                isAdmin: false,
                loading: false,
            });
        }
    }, []);

    // Single fetch on mount. `refetchUser` is stable (empty useCallback deps)
    // so this effect runs once and keeps the initial loading:true state until
    // the session resolves.
    useEffect(() => {
        void refetchUser();
    }, [refetchUser]);

    // Re-derive user state on Amplify auth Hub events. A `tokenRefresh` carries
    // the latest claims (e.g. an updated `custom:userType` after a role change
    // in Cognito), and a `signedIn` marks a fresh session. Other auth events
    // (signOut, failures, etc.) are ignored. `Hub.listen` returns its own
    // unsubscribe function, returned directly for cleanup.
    useEffect(() => {
        const unsubscribe = Hub.listen('auth', ({ payload }) => {
            if (payload.event === 'tokenRefresh' || payload.event === 'signedIn') {
                void refetchUser();
            }
        });
        return unsubscribe;
    }, [refetchUser]);

    return <CurrentUserContext.Provider value={state}>{children}</CurrentUserContext.Provider>;
}
